import { displayFileNameFromObjectKey } from "@quovy/field-types/lib/file-name";

export { displayFileNameFromObjectKey, FILE_KEY_PREFIX_RE } from "@quovy/field-types/lib/file-name";

const OFFICE_FILE_CONTENT_TYPES = [
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-excel.sheet.macroenabled.12",
  "application/vnd.ms-excel.sheet.binary.macroenabled.12",
  "application/vnd.ms-powerpoint",
  "application/vnd.ms-powerpoint.presentation.macroenabled.12",
  "application/vnd.ms-word.document.macroenabled.12",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.oasis.opendocument.presentation",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.ms-outlook",
];

const TEXT_FILE_CONTENT_TYPES = [
  "application/json",
  "application/ld+json",
  "application/rtf",
  "application/vnd.ms-outlook",
  "application/x-ndjson",
  "application/x-yaml",
  "application/xml",
  "application/yaml",
  "message/rfc822",
  "text/csv",
  "text/html",
  "text/markdown",
  "text/plain",
  "text/rtf",
  "text/tab-separated-values",
  "text/xml",
];

const IMAGE_FILE_CONTENT_TYPES = [
  "image/bmp",
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/tiff",
  "image/webp",
];

export const SUPPORTED_FILE_CONTENT_TYPES = [
  "application/pdf",
  "application/zip",
  ...TEXT_FILE_CONTENT_TYPES,
  ...OFFICE_FILE_CONTENT_TYPES,
  ...IMAGE_FILE_CONTENT_TYPES,
] as const;

export const SUPPORTED_FILE_EXTENSIONS = [
  ".bmp",
  ".csv",
  ".doc",
  ".docm",
  ".docx",
  ".eml",
  ".gif",
  ".htm",
  ".html",
  ".jpeg",
  ".jpg",
  ".json",
  ".md",
  ".msg",
  ".odp",
  ".ods",
  ".odt",
  ".pdf",
  ".png",
  ".ppt",
  ".pptm",
  ".pptx",
  ".rtf",
  ".tif",
  ".tiff",
  ".tsv",
  ".txt",
  ".webp",
  ".xls",
  ".xlsb",
  ".xlsm",
  ".xlsx",
  ".xml",
  ".zip",
  ".yaml",
  ".yml",
] as const;

const EXTENSION_TO_CONTENT_TYPE: Record<string, string> = {
  ".bmp": "image/bmp",
  ".csv": "text/csv",
  ".doc": "application/msword",
  ".docm": "application/vnd.ms-word.document.macroenabled.12",
  ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ".eml": "message/rfc822",
  ".gif": "image/gif",
  ".htm": "text/html",
  ".html": "text/html",
  ".jpeg": "image/jpeg",
  ".jpg": "image/jpeg",
  ".json": "application/json",
  ".md": "text/markdown",
  ".msg": "application/vnd.ms-outlook",
  ".odp": "application/vnd.oasis.opendocument.presentation",
  ".ods": "application/vnd.oasis.opendocument.spreadsheet",
  ".odt": "application/vnd.oasis.opendocument.text",
  ".pdf": "application/pdf",
  ".png": "image/png",
  ".ppt": "application/vnd.ms-powerpoint",
  ".pptm": "application/vnd.ms-powerpoint.presentation.macroenabled.12",
  ".pptx": "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ".rtf": "application/rtf",
  ".tif": "image/tiff",
  ".tiff": "image/tiff",
  ".tsv": "text/tab-separated-values",
  ".txt": "text/plain",
  ".webp": "image/webp",
  ".xls": "application/vnd.ms-excel",
  ".xlsb": "application/vnd.ms-excel.sheet.binary.macroenabled.12",
  ".xlsm": "application/vnd.ms-excel.sheet.macroenabled.12",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ".xml": "application/xml",
  ".zip": "application/zip",
  ".yaml": "application/yaml",
  ".yml": "application/yaml",
};

// `binary/octet-stream` is S3's legacy default for objects stored without an
// explicit Content-Type, so presigned-URL inputs must reach the extension fallback.
const GENERIC_CONTENT_TYPES = new Set(["", "application/octet-stream", "binary/octet-stream"]);
const TEXT_LIKE_CONTENT_TYPES = new Set(TEXT_FILE_CONTENT_TYPES);
const IMAGE_CONTENT_TYPES = new Set(IMAGE_FILE_CONTENT_TYPES);
const SUPPORTED_CONTENT_TYPE_SET = new Set<string>(SUPPORTED_FILE_CONTENT_TYPES);
const SUPPORTED_EXTENSION_SET = new Set<string>(SUPPORTED_FILE_EXTENSIONS);

// ---------------------------------------------------------------------------
// Script files — a SEPARATE, narrower allowlist that is deliberately NOT part
// of `SUPPORTED_FILE_*`. Scripts are runnable source, so they must never be
// accepted on the general upload surfaces (submissions, emails, chat, etc.).
// They are permitted ONLY for agent *reference files*, which get staged into
// the agent's sandboxed workspace as source material the agent can read and
// run there. Nothing on the API/server side ever executes these bytes.
//
// Resolution for scripts is keyed off the extension, not the declared MIME
// type: browser/OS content-type sniffing for source code is unreliable (e.g.
// `.ts` is frequently reported as `video/mp2t`, `.py` as
// `application/octet-stream`). The extension is the trustworthy signal.
// ---------------------------------------------------------------------------

const SCRIPT_EXTENSION_TO_CONTENT_TYPE: Record<string, string> = {
  ".py": "text/x-python",
  ".sh": "application/x-sh",
  ".bash": "application/x-sh",
  ".zsh": "application/x-sh",
  ".js": "text/javascript",
  ".mjs": "text/javascript",
  ".cjs": "text/javascript",
  ".jsx": "text/jsx",
  ".ts": "application/typescript",
  ".tsx": "text/tsx",
  ".rb": "text/x-ruby",
  ".pl": "text/x-perl",
  ".php": "application/x-httpd-php",
  ".ps1": "application/x-powershell",
  ".r": "text/x-r",
  ".lua": "text/x-lua",
  ".sql": "application/sql",
};

export const SCRIPT_FILE_EXTENSIONS = Object.keys(
  SCRIPT_EXTENSION_TO_CONTENT_TYPE,
) as readonly string[];

export const SCRIPT_FILE_CONTENT_TYPES = [
  ...new Set(Object.values(SCRIPT_EXTENSION_TO_CONTENT_TYPE)),
] as readonly string[];

const SCRIPT_EXTENSION_SET = new Set<string>(SCRIPT_FILE_EXTENSIONS);

export const FILE_INPUT_ACCEPT = SUPPORTED_FILE_EXTENSIONS.join(",");

/**
 * `accept` list for the agent reference-file picker: the standard document
 * types PLUS script formats. Distinct from `FILE_INPUT_ACCEPT` so script
 * uploads stay scoped to agent source material and never leak onto the
 * general document-upload surfaces.
 */
export const AGENT_REFERENCE_FILE_INPUT_ACCEPT = [
  ...SUPPORTED_FILE_EXTENSIONS,
  ...SCRIPT_FILE_EXTENSIONS,
].join(",");

/**
 * Hard cap on a single upload's size, enforced at two layers:
 *  - browser pre-flight before `POST /internal/dashboard/files/presign`,
 *  - server validation of `file_size` in the presign request.
 *
 * Direct-S3 PUTs bypass the ALB (and the WAF body-size rule that used to
 * accidentally cap uploads at 8 KiB), so this is now the only cap.
 * 100 MiB comfortably covers the largest real insurance submission
 * artifacts we've seen (multi-tab loss-run XLSX, scanned binders) without
 * blowing up downstream parsing memory.
 */
export const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

/**
 * Cap for server-side `.eml`/`.msg` email-file imports (`POST /v1/email/import`).
 * Deliberately far below {@link MAX_FILE_SIZE_BYTES}: unlike a plain `/v1/files`
 * upload (bytes stored as-is), an imported email is fully parsed in memory —
 * mailparser/msgreader build a parse tree with every attachment materialized and
 * `.msg` decompresses attacker-controlled RTF — so a 100 MiB cap would let a
 * single authenticated request allocate a large multiple of that in RSS. 30 MiB
 * comfortably covers real provider message-size limits (Gmail/Outlook/Mailgun
 * ~25 MiB, base64/MIME overhead included) while bounding the parse amplification.
 */
export const EMAIL_IMPORT_MAX_FILE_SIZE_BYTES = 30 * 1024 * 1024;

export function normalizeContentType(contentType: string | null | undefined) {
  return contentType?.split(";")[0]?.trim().toLowerCase() ?? "";
}

export function getFileExtension(fileName: string) {
  const lastDot = fileName.lastIndexOf(".");
  return lastDot >= 0 ? fileName.slice(lastDot).toLowerCase() : "";
}

export function isSupportedFileContentType(contentType: string | null | undefined) {
  return SUPPORTED_CONTENT_TYPE_SET.has(normalizeContentType(contentType));
}

export function isSupportedFileExtension(fileName: string) {
  return SUPPORTED_EXTENSION_SET.has(getFileExtension(fileName));
}

export function isSupportedFile(fileName: string, contentType: string | null | undefined) {
  const normalized = normalizeContentType(contentType);
  if (isSupportedFileContentType(normalized)) {
    return true;
  }

  return GENERIC_CONTENT_TYPES.has(normalized) && isSupportedFileExtension(fileName);
}

export function resolveFileContentType(
  fileName: string,
  contentType: string | null | undefined,
): string | null {
  const normalized = normalizeContentType(contentType);
  if (SUPPORTED_CONTENT_TYPE_SET.has(normalized)) {
    return normalized;
  }

  if (!GENERIC_CONTENT_TYPES.has(normalized)) {
    return null;
  }

  return EXTENSION_TO_CONTENT_TYPE[getFileExtension(fileName)] ?? null;
}

/**
 * An email message file — `.eml` (message/rfc822) or `.msg` (Outlook). Detected
 * by content-type OR extension so it stays true even when a browser reports an
 * `.eml` as `text/plain`. Mirrors `isEmailMessageFilename` in `@quovy/email-core`
 * (the backend guard, which shares `normalizeContentType` so both sides treat
 * parameterized types like `message/rfc822; charset=utf-8` identically) but is
 * dependency-free for frontend use.
 */
export function isEmailFile(fileName: string | null | undefined, contentType?: string | null) {
  const normalized = normalizeContentType(contentType);
  if (normalized === "message/rfc822" || normalized === "application/vnd.ms-outlook") {
    return true;
  }
  if (!fileName) return false;
  const ext = getFileExtension(fileName);
  return ext === ".eml" || ext === ".msg";
}

/**
 * An Outlook `.msg` file specifically (OLE2 binary, unlike the RFC822-text
 * `.eml`). Used to decide whether raw bytes are human-readable.
 */
export function isOutlookMsgFile(fileName: string | null | undefined, contentType?: string | null) {
  if (normalizeContentType(contentType) === "application/vnd.ms-outlook") return true;
  return fileName ? getFileExtension(fileName) === ".msg" : false;
}

/** A file whose extension marks it as a runnable script (see allowlist above). */
export function isScriptFile(fileName: string) {
  return SCRIPT_EXTENSION_SET.has(getFileExtension(fileName));
}

/**
 * Upload gate for agent reference files: the general allowlist PLUS scripts.
 * Scripts are accepted on extension alone because their declared MIME type is
 * unreliable. Use this instead of `isSupportedFile` only on the agent
 * reference-file upload path.
 */
export function isSupportedAgentReferenceFile(
  fileName: string,
  contentType: string | null | undefined,
) {
  return isSupportedFile(fileName, contentType) || isScriptFile(fileName);
}

/**
 * Content-type resolution for agent reference files: falls back to the
 * extension-keyed script table when the general resolver can't place the file.
 * Script extensions win over their (unreliable) declared MIME type.
 */
export function resolveAgentReferenceFileContentType(
  fileName: string,
  contentType: string | null | undefined,
): string | null {
  const scriptType = SCRIPT_EXTENSION_TO_CONTENT_TYPE[getFileExtension(fileName)];
  if (scriptType) {
    return scriptType;
  }
  return resolveFileContentType(fileName, contentType);
}

/**
 * Extension → Shiki/Streamdown language id, used to pick a grammar for the
 * syntax-highlighted code preview. Keyed off the extension because that's the
 * trustworthy signal for source code — browser/OS MIME sniffing mislabels it
 * (`.ts` as `video/mp2t`, `.py` as `application/octet-stream`), the same reason
 * {@link SCRIPT_EXTENSION_TO_CONTENT_TYPE} is extension-keyed. Covers the script
 * allowlist plus the structured text formats that read better highlighted.
 */
const CODE_PREVIEW_LANGUAGE_BY_EXTENSION: Record<string, string> = {
  ".py": "python",
  ".sh": "bash",
  ".bash": "bash",
  ".zsh": "bash",
  ".js": "javascript",
  ".mjs": "javascript",
  ".cjs": "javascript",
  ".jsx": "jsx",
  ".ts": "typescript",
  ".tsx": "tsx",
  ".rb": "ruby",
  ".pl": "perl",
  ".php": "php",
  ".ps1": "powershell",
  ".r": "r",
  ".lua": "lua",
  ".sql": "sql",
  ".json": "json",
  ".yaml": "yaml",
  ".yml": "yaml",
  ".xml": "xml",
  ".html": "html",
  ".htm": "html",
  ".css": "css",
};

/**
 * Resolve a filename to a syntax-highlighting language id for the code
 * preview, or `null` when the file isn't a recognized code/data format (in
 * which case the caller should fall back to the plain text viewer). See
 * {@link CODE_PREVIEW_LANGUAGE_BY_EXTENSION} for why this is keyed off the
 * extension rather than the declared content type.
 */
export function getCodePreviewLanguage(fileName: string | null | undefined): string | null {
  if (!fileName) return null;
  return CODE_PREVIEW_LANGUAGE_BY_EXTENSION[getFileExtension(fileName)] ?? null;
}

export function isPdfContentType(contentType: string | null | undefined) {
  return normalizeContentType(contentType) === "application/pdf";
}

const SPREADSHEET_CONTENT_TYPES = new Set([
  "application/vnd.ms-excel",
  "application/vnd.ms-excel.sheet.macroenabled.12",
  "application/vnd.ms-excel.sheet.binary.macroenabled.12",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.spreadsheet",
  "text/csv",
]);

export function isSpreadsheetContentType(contentType: string | null | undefined) {
  return SPREADSHEET_CONTENT_TYPES.has(normalizeContentType(contentType));
}

const DOCX_CONTENT_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-word.document.macroenabled.12",
]);

export function isDocxContentType(contentType: string | null | undefined) {
  return DOCX_CONTENT_TYPES.has(normalizeContentType(contentType));
}

const PPTX_CONTENT_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-powerpoint.presentation.macroenabled.12",
]);

export function isPptxContentType(contentType: string | null | undefined) {
  return PPTX_CONTENT_TYPES.has(normalizeContentType(contentType));
}

export function isImageContentType(contentType: string | null | undefined) {
  return IMAGE_CONTENT_TYPES.has(normalizeContentType(contentType));
}

const MARKDOWN_CONTENT_TYPES = new Set(["text/markdown", "text/x-markdown"]);

export function isMarkdownContentType(contentType: string | null | undefined) {
  return MARKDOWN_CONTENT_TYPES.has(normalizeContentType(contentType));
}

export function isTextLikeContentType(contentType: string | null | undefined) {
  const normalized = normalizeContentType(contentType);
  return normalized.startsWith("text/") || TEXT_LIKE_CONTENT_TYPES.has(normalized);
}

const INLINE_PREVIEWABLE_IMAGE_RE = /^image\/(png|jpe?g|gif|webp|bmp)$/;

/**
 * Content types safe to serve with `Content-Disposition: inline` from an API
 * origin: raster images, PDF, and plain text. Deliberately narrow — HTML and
 * SVG are active content and would execute against the API's origin, and
 * anything unrecognized could be sniffed into one of those.
 *
 * The API is the security authority: it pairs this allowlist with
 * `X-Content-Type-Options: nosniff` and an `attachment` fallback for
 * everything else. The web app reads the same predicate only to decide
 * whether to offer a preview affordance at all.
 */
export function isInlinePreviewableContentType(contentType: string | null | undefined) {
  const normalized = normalizeContentType(contentType);
  return (
    INLINE_PREVIEWABLE_IMAGE_RE.test(normalized) ||
    normalized === "application/pdf" ||
    normalized === "text/plain"
  );
}

const FILE_SIZE_UNITS = ["B", "KB", "MB", "GB", "TB"];

/**
 * Bytes → a human-readable size. Accepts a number or the numeric string that
 * the storage-provider APIs return, and yields `""` for anything unusable so
 * callers can render it directly into a cell without a null check.
 *
 * Bytes render whole (`938 B`); every larger unit gets one decimal
 * (`1.4 KB`, `12.0 MB`).
 */
export function formatFileSize(bytes: number | string | null | undefined): string {
  if (bytes === null || bytes === undefined || bytes === "") return "";
  const n = typeof bytes === "number" ? bytes : Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "";

  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < FILE_SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${FILE_SIZE_UNITS[unit]}`;
}

/**
 * Map a MIME content type to a short, UPPERCASE, display-friendly label
 * (e.g. `PDF`, `XLSX`, `EML`). Designed for table cells and badges where
 * the raw
 * `application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`
 * would blow out the column.
 *
 * Resolution:
 *   1. Exact-match MIME table (covers every type in
 *      `SUPPORTED_FILE_CONTENT_TYPES`).
 *   2. For unknown types, fall back to the filename extension if
 *      provided (so legacy `application/octet-stream` rows with a
 *      `.pdf` name still render as `PDF`).
 *   3. Final fallback: the part after `/`, uppercased, truncated to 6
 *      chars — lossy but at least readable.
 *   4. Empty / missing → `FILE`.
 */
const CONTENT_TYPE_TO_LABEL: Record<string, string> = {
  "application/pdf": "PDF",
  "application/zip": "ZIP",
  "application/json": "JSON",
  "application/ld+json": "JSON",
  "application/x-ndjson": "NDJSON",
  "application/rtf": "RTF",
  "application/xml": "XML",
  "application/yaml": "YAML",
  "application/x-yaml": "YAML",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "application/vnd.ms-excel": "XLS",
  "application/vnd.ms-excel.sheet.macroenabled.12": "XLSM",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/vnd.ms-powerpoint": "PPT",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
  "application/vnd.oasis.opendocument.presentation": "ODP",
  "application/vnd.oasis.opendocument.spreadsheet": "ODS",
  "application/vnd.oasis.opendocument.text": "ODT",
  "application/vnd.ms-outlook": "MSG",
  "message/rfc822": "EML",
  "text/csv": "CSV",
  "text/html": "HTML",
  "text/markdown": "MD",
  "text/plain": "TXT",
  "text/rtf": "RTF",
  "text/tab-separated-values": "TSV",
  "text/xml": "XML",
  "image/bmp": "BMP",
  "image/gif": "GIF",
  "image/jpeg": "JPG",
  "image/png": "PNG",
  "image/tiff": "TIFF",
  "image/webp": "WEBP",
};

export function getContentTypeLabel(
  contentType: string | null | undefined,
  fileName?: string | null,
): string {
  const normalized = normalizeContentType(contentType);
  if (normalized && CONTENT_TYPE_TO_LABEL[normalized]) {
    return CONTENT_TYPE_TO_LABEL[normalized]!;
  }

  if (fileName) {
    const ext = getFileExtension(fileName);
    const mapped = EXTENSION_TO_CONTENT_TYPE[ext];
    if (mapped && CONTENT_TYPE_TO_LABEL[mapped]) {
      return CONTENT_TYPE_TO_LABEL[mapped]!;
    }
    if (ext) {
      return ext.slice(1).toUpperCase().slice(0, 6);
    }
  }

  if (normalized) {
    const tail = normalized.split("/").pop() ?? "";
    if (tail) {
      return tail.toUpperCase().slice(0, 6);
    }
  }

  return "FILE";
}

/**
 * Canonical user-facing filename for a `files` row. Prefer this over
 * parsing `objectKey`. Resolution order:
 *   1. `file.filename` — the row's first-class column.
 *   2. `displayFileNameFromObjectKey(objectKey)` — strips the canonical
 *      `<timestamp>-<uuid>-` prefix from a storage key.
 *   3. `objectKey` itself — raw fallback so the function never returns ""
 *      when the caller has anything at all.
 */
export function displayFileName(file: {
  filename?: string | null;
  objectKey?: string | null;
}): string {
  if (file.filename && file.filename.length > 0) return file.filename;
  if (!file.objectKey) return "";
  const stripped = displayFileNameFromObjectKey(file.objectKey);
  return stripped || file.objectKey;
}

/** Add a provider version label without changing the filename extension. */
export function formatVersionedFileName(fileName: string, versionId: string): string {
  const safeVersion = versionId.replace(/[^a-zA-Z0-9._-]/g, "_");
  const extensionAt = fileName.lastIndexOf(".");
  if (extensionAt <= 0) return `${fileName} (version ${safeVersion})`;
  return `${fileName.slice(0, extensionAt)} (version ${safeVersion})${fileName.slice(extensionAt)}`;
}

// ---------------------------------------------------------------------------
// Remote item identity
// ---------------------------------------------------------------------------

/**
 * The id spaces a file identifies in — provider values match what an import
 * stamps on `files.source_kind`, so an id joins to `files.source_ref`.
 */
export const ITEM_ID_SOURCES = {
  /** SharePoint and OneDrive alike — both are Graph driveItems. */
  MICROSOFT_GRAPH: "microsoft-graph",
  GOOGLE_DRIVE: "google-drive",
  /** A workspace file, identified by its own `fil_…` public id. */
  QUOVY_FILE: "quovy-file",
} as const;

export type ItemIdSource = (typeof ITEM_ID_SOURCES)[keyof typeof ITEM_ID_SOURCES];

/**
 * A file's stable identity: the source's own raw item id (a Graph driveItem id,
 * a Drive file id, a `fil_…` public id), plus the id space it lives in.
 */
export type ItemIdentity = {
  id: string;
  sourceKind: ItemIdSource;
  /**
   * The container `id` is unique within — a Graph drive, whose ids are only
   * drive-local. Absent for a source that issues global ids.
   */
  scope?: string;
};

/**
 * Pair a raw item id with its id space, or nothing when the source gave none.
 * Pass `scope` null only for a source whose ids are global.
 */
export function itemIdentity(
  sourceKind: ItemIdSource,
  rawId: string | null | undefined,
  scope: string | null,
): ItemIdentity | undefined {
  const id = rawId?.trim();
  if (!id) return undefined;
  return { id, sourceKind, ...(scope ? { scope } : {}) };
}
